import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, Menu, X, ChevronDown, Sun, Moon, Loader2, Zap } from "lucide-react";
import { useState, useEffect } from "react";
import logoImage from "@/assets/logo.png";
import { useTheme, THEMES } from "@/contexts/ThemeContext";
import { usePolling } from "@/contexts/PollingContext";
import api from "../lib/api";
import { CREDITS_CHANGED_EVENT } from "@/pages/spark_creator/assetDrag";
import { getConnectAppUrl } from "@/lib/connectUrl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";


const Navbar = ({ isAuthenticated, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { isPolling, pollingJobs } = usePolling();
  const [creditBalance, setCreditBalance] = useState(
    typeof user?.credit_balance === "number" ? user.credit_balance : null
  );

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    if (typeof user?.credit_balance === "number") {
      setCreditBalance(user.credit_balance);
    }
  }, [user?.credit_balance]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchBalance = () => {
      api.get(`/billing/credits`)
        .then(r => setCreditBalance(r.data.balance))
        .catch(() => {});
    };
    const onCreditsChanged = (event) => {
      const next = event?.detail?.balance;
      if (typeof next === "number" && Number.isFinite(next)) {
        setCreditBalance(next);
        return;
      }
      fetchBalance();
    };
    fetchBalance();
    window.addEventListener(CREDITS_CHANGED_EVENT, onCreditsChanged);
    const timer = setInterval(fetchBalance, 60000);
    return () => {
      window.removeEventListener(CREDITS_CHANGED_EVENT, onCreditsChanged);
      clearInterval(timer);
    };
  }, [isAuthenticated]);

  const scrollToSection = (sectionId) => {
    if (location.pathname === '/' || location.pathname === '/home') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/home#${sectionId}`);
    }
    setMobileMenuOpen(false);
  };

  const handleHomeClick = (e) => {
    if (location.pathname === '/' || location.pathname === '/home') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav className="creator-shell fixed top-0 left-0 right-0 z-50 h-16 border-b border-neutral-200 dark:border-white/10 bg-white/90 dark:bg-neutral-950/90 backdrop-blur-md">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to={isAuthenticated ? '/spark-creator' : '/home'} onClick={isAuthenticated ? undefined : handleHomeClick} className="flex items-center group">
            <img src={logoImage} alt="SparkSeller Creator" className="h-12 group-hover:opacity-90 transition-opacity" />
          </Link>

          {!isAuthenticated ? (
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/home" onClick={handleHomeClick} className={`text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors ${isActive('/home') || isActive('/') ? 'text-neutral-900 dark:text-white font-semibold' : ''}`}>
                Home
              </Link>
              <button
                onClick={() => scrollToSection('how-we-help')}
                className="text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors"
              >
                Contact
              </button>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
                title={THEMES[theme]?.dark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {THEMES[theme]?.dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="rounded-full border-neutral-200 dark:border-white/10 text-neutral-700 dark:text-white hover:bg-neutral-100 dark:hover:bg-white/5"
              >
                Login
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                className="rounded-full bg-orange-500 hover:bg-orange-400 text-black font-medium"
              >
                Get Started
              </Button>
            </div>
          ) : (
            <div className="hidden md:flex items-center space-x-6">
              {user?.can_access_tools && (
                <button
                  onClick={() => navigate('/spark-creator')}
                  className={`text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors ${location.pathname.startsWith('/spark-creator') ? 'text-neutral-900 dark:text-white font-semibold' : ''}`}
                >
                  Creator
                </button>
              )}

              {isPolling && (
                pollingJobs.length === 1 ? (
                  <button
                    onClick={() => pollingJobs[0].onReopen?.()}
                    title={`${pollingJobs[0].label}${pollingJobs[0].phase ? ` — ${pollingJobs[0].phase}` : ''} — click to view`}
                    className="relative p-2 rounded-full text-orange-500 hover:text-orange-400 hover:bg-orange-500/10 transition-colors"
                  >
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        title={`${pollingJobs.length} tasks running — click to view`}
                        className="relative p-2 rounded-full text-orange-500 hover:text-orange-400 hover:bg-orange-500/10 transition-colors"
                      >
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="absolute -top-0.5 -right-0.5 bg-orange-500 text-black text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                          {pollingJobs.length}
                        </span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white dark:bg-neutral-900 border-neutral-200 dark:border-white/10">
                      {pollingJobs.map(job => (
                        <DropdownMenuItem
                          key={job.id}
                          className="text-neutral-700 dark:text-white/80 hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                          onClick={() => job.onReopen?.()}
                        >
                          <Loader2 className="w-3 h-3 mr-2 text-orange-500 animate-spin" />
                          <span className="flex flex-col">
                            <span className="text-sm">{job.label}</span>
                            {job.phase && <span className="text-xs text-neutral-500 dark:text-white/40">{job.phase}</span>}
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              )}

              <a
                href={getConnectAppUrl()}
                className="text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors"
                title="Open SparkSeller Connect"
              >
                Connect
              </a>

              {isAuthenticated && creditBalance !== null && (
                <button
                  onClick={() => navigate('/credits')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500 text-black text-sm font-medium hover:bg-orange-400"
                  title="Credits — click to buy more"
                >
                  <Zap className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold">
                    {creditBalance.toLocaleString()}
                  </span>
                </button>
              )}

              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
                title={THEMES[theme]?.dark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {THEMES[theme]?.dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              <div className="flex items-center space-x-3 pl-4 border-l border-neutral-200 dark:border-white/10">
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center space-x-2 focus:outline-none hover:opacity-80 transition-opacity cursor-pointer">
                    <span className="text-sm text-neutral-500 dark:text-white/60">Hi, {user?.first_name || user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-neutral-400 dark:text-white/40" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white dark:bg-neutral-900 border-neutral-200 dark:border-white/10 min-w-[160px]">
                    <DropdownMenuItem
                      className="text-neutral-700 dark:text-white/80 hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                      onClick={() => navigate('/spark-creator/settings')}
                    >
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-orange-600 dark:text-orange-400 hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                      onClick={() => navigate('/credits')}
                    >
                      Buy Credits <Zap className="w-3.5 h-3.5 mx-1.5 flex-shrink-0" /> {creditBalance !== null && <span className="ml-auto text-xs text-neutral-400">({creditBalance.toLocaleString()})</span>}
                    </DropdownMenuItem>
                    {user?.is_admin && (
                      <DropdownMenuItem
                        className="text-amber-600 dark:text-amber-400 hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                        onClick={() => navigate('/admin')}
                      >
                        Admin Dashboard
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLogout}
                  className="text-neutral-500 dark:text-white/50 hover:text-neutral-900 dark:hover:text-white"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          <button
            className="md:hidden text-neutral-900 dark:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-neutral-200 dark:border-white/10 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {!isAuthenticated ? (
              <div className="flex flex-col space-y-3">
                <Link to="/home" className="text-neutral-600 dark:text-white/70 hover:text-neutral-900 dark:hover:text-white py-2" onClick={handleHomeClick}>
                  Home
                </Link>
                <button
                  onClick={() => scrollToSection('how-we-help')}
                  className="text-neutral-600 dark:text-white/70 hover:text-neutral-900 dark:hover:text-white py-2 text-left"
                >
                  Features
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-neutral-600 dark:text-white/70 hover:text-neutral-900 dark:hover:text-white py-2 text-left"
                >
                  Contact
                </button>
                <button
                  onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
                  className="flex items-center text-neutral-600 dark:text-white/70 hover:text-neutral-900 dark:hover:text-white py-2"
                >
                  {THEMES[theme]?.dark ? <Sun className="w-5 h-5 mr-2" /> : <Moon className="w-5 h-5 mr-2" />}
                  {THEMES[theme]?.dark ? 'Light Mode' : 'Dark Mode'}
                </button>
                <Button variant="outline" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="w-full">
                  Login
                </Button>
                <Button onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }} className="w-full rounded-full bg-orange-500 hover:bg-orange-400 text-black font-medium">
                  Get Started
                </Button>
              </div>
            ) : (
              <div className="flex flex-col space-y-3">
                {user?.can_access_tools && (
                  <Link to="/spark-creator" className="text-neutral-600 dark:text-white/70 hover:text-neutral-900 dark:hover:text-white py-2 block" onClick={() => setMobileMenuOpen(false)}>
                    Creator
                  </Link>
                )}
                <a
                  href={getConnectAppUrl()}
                  className="text-neutral-600 dark:text-white/70 hover:text-neutral-900 dark:hover:text-white py-2 block"
                  title="Open SparkSeller Connect"
                >
                  Connect
                </a>
                {user?.is_admin && (
                  <Link to="/admin" className="text-neutral-600 dark:text-white/70 hover:text-neutral-900 dark:hover:text-white py-2 block" onClick={() => setMobileMenuOpen(false)}>
                    Admin Dashboard
                  </Link>
                )}
                <Link to="/spark-creator/settings" className="text-neutral-600 dark:text-white/70 hover:text-neutral-900 dark:hover:text-white py-2 block" onClick={() => setMobileMenuOpen(false)}>
                  Settings
                </Link>
                <Link
                  to="/credits"
                  className="flex items-center gap-2 py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500 text-black hover:bg-orange-400 transition-colors">
                    <Zap className="w-3.5 h-3.5" />
                    <span className="text-xs font-semibold">
                      {creditBalance !== null ? creditBalance.toLocaleString() : '—'} Credits
                    </span>
                  </span>
                  <span className="text-xs text-neutral-400 dark:text-white/40">tap to buy more</span>
                </Link>
                <button
                  onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
                  className="flex items-center text-neutral-600 dark:text-white/70 hover:text-neutral-900 dark:hover:text-white py-2"
                >
                  {THEMES[theme]?.dark ? <Sun className="w-5 h-5 mr-2" /> : <Moon className="w-5 h-5 mr-2" />}
                  {THEMES[theme]?.dark ? 'Light Mode' : 'Dark Mode'}
                </button>
                {isPolling && (
                  <div className="py-2 space-y-1">
                    {pollingJobs.map(job => (
                      <button
                        key={job.id}
                        onClick={() => { job.onReopen?.(); setMobileMenuOpen(false); }}
                        className="flex items-center w-full text-orange-500 hover:text-orange-400 py-1"
                      >
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        <span className="text-sm">{job.label}</span>
                        {job.phase && <span className="text-xs text-neutral-500 ml-2">{job.phase}</span>}
                      </button>
                    ))}
                  </div>
                )}
                <div className="py-3 border-t border-neutral-200 dark:border-white/10 mt-2">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="text-sm text-neutral-500 dark:text-white/60">Hi, {user?.first_name || user?.name}</span>
                  </div>
                  <Button variant="outline" onClick={() => { onLogout(); setMobileMenuOpen(false); }} className="w-full">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
